import { useCallback, useEffect, useMemo, useState } from 'react'
import { Report } from 'notiflix'
import api from '../../../helpers/apiRequester'
import { Banner, Button, Card, Checkbox, Field, Input, NativeSelect, StatCard, StatGrid, StatusPill, Toolbar } from '../../../ui'
import styles from '../commsShared/admin.module.scss'

interface Model {
    key: string
    provider: 'openai' | 'bedrock'
    modelId: string
    displayName: string | null
    tier: string
    inputCostPerMtokUsd: number | null
    outputCostPerMtokUsd: number | null
    region: string | null
    dataResidency: string | null
    active: boolean
    notes: string | null
}

interface Route {
    task: string
    primaryModel: string
    pairModel: string | null
    fallbackModel: string | null
    tiebreakModel: string | null
    escalationModel: string | null
    shadowModel: string | null
    voting: 'single' | 'pair' | 'pair_plus_tiebreak'
    escalateBelowConfidence: number | null
    promptVersion: string | null
    schemaVersion: string | null
    cacheIdenticalInputs: boolean
    active: boolean
    notes: string | null
    updatedBy: string | null
    updatedAt: string | null
}

interface Stat {
    task: string
    modelKey: string
    role: string
    runs: number
    ok: number
    errors: number
    invalid: number
    cached: number
    inputTokens: number
    outputTokens: number
    costPence: number
    costUsd: number
    avgLatencyMs: number
    lastCallAt: string | null
}

const SLOTS: { key: keyof Route; label: string; hint: string }[] = [
    { key: 'primaryModel', label: 'Primary', hint: 'Always called' },
    { key: 'pairModel', label: 'Pair', hint: 'Second opinion (pair voting)' },
    { key: 'tiebreakModel', label: 'Tie-break', hint: 'Only asked about disputed fields' },
    { key: 'fallbackModel', label: 'Fallback', hint: 'When the primary or pair errors' },
    { key: 'escalationModel', label: 'Escalation', hint: 'When confidence is below the threshold' },
    { key: 'shadowModel', label: 'Shadow', hint: 'Runs silently for comparison' },
]

const pence = (v: number) => `£${(v / 100).toFixed(2)}`
const fmt = (n: number) => n.toLocaleString('en-GB')
const fmtDate = (raw: string | null) => (raw ? new Date(raw).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—')

export const CommsModelsView = () => {
    const [models, setModels] = useState<Model[]>([])
    const [routes, setRoutes] = useState<Route[]>([])
    const [stats, setStats] = useState<Stat[]>([])
    const [source, setSource] = useState<string>('')
    const [autoExtract, setAutoExtract] = useState<{ enabled: boolean; source: string; secretName: string } | null>(null)
    const [sinceDays, setSinceDays] = useState(30)
    const [busy, setBusy] = useState<string | null>(null)
    const [drafts, setDrafts] = useState<Record<string, Partial<Route>>>({})

    const load = useCallback(async () => {
        try {
            const [m, s] = await Promise.all([api({ url: 'comms/models/list', data: {} }), api({ url: 'comms/models/stats', data: { sinceDays } })])
            if (m.data?.success) {
                setModels(m.data.models || [])
                setRoutes(m.data.routes || [])
                setSource(m.data.source)
                setAutoExtract(m.data.autoExtract || null)
            }
            if (s.data?.success) setStats(s.data.stats || [])
        } catch (err: any) {
            Report.failure('Load failed', err?.response?.data?.message || 'Could not load models.', 'Okay')
        }
    }, [sinceDays])

    useEffect(() => {
        load()
    }, [load])

    const post = async (label: string, url: string, data: Record<string, unknown>) => {
        setBusy(label)
        try {
            const resp = await api({ url, data })
            if (resp.data?.success === false) Report.warning(label, resp.data.message || 'Did not complete.', 'Okay')
            await load()
            return resp.data
        } catch (err: any) {
            Report.failure(`${label} failed`, err?.response?.data?.message || 'Please try again.', 'Okay')
        } finally {
            setBusy(null)
        }
    }

    const draftFor = (r: Route): Route => ({ ...r, ...(drafts[r.task] || {}) })
    const setDraft = (task: string, patch: Partial<Route>) => setDrafts((p) => ({ ...p, [task]: { ...(p[task] || {}), ...patch } }))
    const isDirty = (task: string) => Boolean(drafts[task] && Object.keys(drafts[task]).length)
    const readOnly = source === 'defaults'

    const saveRoute = async (task: string) => {
        const patch = drafts[task]
        if (!patch) return
        const data = await post('Save route', 'comms/models/set-route', { task, ...patch })
        if (data?.success) {
            setDrafts((p) => {
                const next = { ...p }
                delete next[task]
                return next
            })
            Report.success('Route updated', `${task} now uses the new routing (takes effect within a minute).`, 'Okay')
        }
    }

    const byModel = useMemo(() => {
        const agg: Record<string, { runs: number; ok: number; errors: number; invalid: number; cached: number; costPence: number; inputTokens: number; outputTokens: number; latency: number; n: number }> = {}
        for (const s of stats) {
            const a = (agg[s.modelKey] ||= { runs: 0, ok: 0, errors: 0, invalid: 0, cached: 0, costPence: 0, inputTokens: 0, outputTokens: 0, latency: 0, n: 0 })
            a.runs += s.runs
            a.ok += s.ok
            a.errors += s.errors
            a.invalid += s.invalid
            a.cached += s.cached
            a.costPence += s.costPence
            a.inputTokens += s.inputTokens
            a.outputTokens += s.outputTokens
            a.latency += s.avgLatencyMs * s.runs
            a.n += s.runs
        }
        return agg
    }, [stats])

    const totalSpend = stats.reduce((a, s) => a + s.costPence, 0)
    const totalRuns = stats.reduce((a, s) => a + s.runs, 0)
    const totalCached = stats.reduce((a, s) => a + s.cached, 0)
    const totalBad = stats.reduce((a, s) => a + s.errors + s.invalid, 0)
    const activeModels = models.filter((m) => m.active)
    const modelOptions = models.map((m) => ({ value: m.key, label: `${m.key}${m.active ? '' : ' (inactive)'}`, disabled: !m.active }))

    return (
        <div className={styles.stack}>
            <StatGrid>
                <StatCard label="Spend" value={pence(totalSpend)} format={false} sub={`last ${sinceDays} days`} />
                <StatCard label="Model calls" value={totalRuns} sub={`${fmt(totalCached)} served from cache`} />
                <StatCard label="Error / invalid rate" value={totalRuns ? Math.round((totalBad / totalRuns) * 1000) / 10 : '—'} unit={totalRuns ? '%' : undefined} tone={totalBad > 0 ? 'warn' : 'default'} />
                <StatCard label="Active models" value={activeModels.length} sub={`registry from ${source || '…'}`} />
            </StatGrid>

            <Toolbar
                end={
                    <Button size="sm" onClick={load} disabled={busy !== null}>
                        Refresh
                    </Button>
                }
            >
                <Field label="Window" inline>
                    {(id) => <NativeSelect id={id} small value={sinceDays} onChange={(e) => setSinceDays(Number(e.target.value))} options={[7, 30, 90].map((d) => ({ value: d, label: `${d} days` }))} />}
                </Field>
            </Toolbar>

            {autoExtract && (
                <Banner tone={autoExtract.enabled ? 'good' : 'neutral'} title={`Automatic extraction is ${autoExtract.enabled ? 'ON' : 'OFF'}`}>
                    {autoExtract.source === 'env'
                        ? 'Set by the COMMS_LLM_EXTRACT_ENABLED environment variable on this server.'
                        : `Controlled by the Secrets Manager secret "${autoExtract.secretName}" (key llmExtractEnabled). Changes take effect within a minute, no redeploy needed.`}{' '}
                    When off, new emails are ingested and matched but wait with extraction pending; use Re-extract on an email or the extract-existing-inbound script to run them by hand.
                </Banner>
            )}

            {readOnly && (
                <Banner tone="warn" title="Read-only">
                    The ai_model tables have not been applied yet — showing built-in defaults. Routes cannot be edited until the migration runs.
                </Banner>
            )}

            <Card as="section" title="Task routing">
                <p className={styles.note}>
                    Each task picks its models here. <strong>Pair + tie-break</strong> runs two cheap models, accepts fields they agree on, and only pays a stronger model to settle the fields they dispute.
                    <strong> Single</strong> is cheapest; <strong>pair</strong> without tie-break leaves disputes for a human.
                </p>
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Task</th>
                                <th>Voting</th>
                                {SLOTS.map((s) => (
                                    <th key={s.key} title={s.hint}>
                                        {s.label}
                                    </th>
                                ))}
                                <th title="Escalate to the escalation model when the primary's confidence is below this">Escalate &lt;</th>
                                <th>Cache</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {routes.map((r) => {
                                const d = draftFor(r)
                                return (
                                    <tr key={r.task}>
                                        <td>
                                            <span className={styles.mono}>{r.task}</span>
                                            <div className={styles.sub}>
                                                {r.promptVersion ? `prompt ${r.promptVersion}` : ''}
                                                {r.updatedBy ? ` · ${r.updatedBy} ${fmtDate(r.updatedAt)}` : ''}
                                            </div>
                                        </td>
                                        <td>
                                            <NativeSelect
                                                small
                                                className={styles.routeSelect}
                                                aria-label={`Voting for ${r.task}`}
                                                value={d.voting}
                                                disabled={readOnly}
                                                onChange={(e) => setDraft(r.task, { voting: e.target.value as Route['voting'] })}
                                                options={[
                                                    { value: 'single', label: 'single' },
                                                    { value: 'pair', label: 'pair' },
                                                    { value: 'pair_plus_tiebreak', label: 'pair + tie-break' },
                                                ]}
                                            />
                                        </td>
                                        {SLOTS.map((s) => (
                                            <td key={s.key}>
                                                <NativeSelect small className={styles.routeSelect} aria-label={`${s.label} model for ${r.task}`} value={(d[s.key] as string | null) || ''} disabled={readOnly} onChange={(e) => setDraft(r.task, { [s.key]: e.target.value || null } as Partial<Route>)}>
                                                    {s.key !== 'primaryModel' && <option value="">—</option>}
                                                    {modelOptions.map((o) => (
                                                        <option key={o.value} value={o.value} disabled={o.disabled}>
                                                            {o.label}
                                                        </option>
                                                    ))}
                                                </NativeSelect>
                                            </td>
                                        ))}
                                        <td>
                                            <Input small className={styles.narrow} aria-label={`Escalation threshold for ${r.task}`} type="number" min={0} max={100} value={d.escalateBelowConfidence ?? ''} disabled={readOnly} onChange={(e) => setDraft(r.task, { escalateBelowConfidence: e.target.value === '' ? null : Number(e.target.value) })} />
                                        </td>
                                        <td>
                                            <Checkbox label="" aria-label={`Cache identical inputs for ${r.task}`} checked={d.cacheIdenticalInputs} disabled={readOnly} onChange={(e) => setDraft(r.task, { cacheIdenticalInputs: e.target.checked })} />
                                        </td>
                                        <td>
                                            {isDirty(r.task) && (
                                                <div className={styles.rowActions}>
                                                    <Button size="sm" variant="primary" disabled={busy !== null} onClick={() => saveRoute(r.task)}>
                                                        Save
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="link"
                                                        onClick={() =>
                                                            setDrafts((p) => {
                                                                const next = { ...p }
                                                                delete next[r.task]
                                                                return next
                                                            })
                                                        }
                                                    >
                                                        Reset
                                                    </Button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </Card>

            <Card as="section" title="Models" subtitle="Bedrock models run in eu-west-2 (data stays in the UK region); OpenAI calls go to OpenAI's API. Prices are USD per million tokens; spend is converted to pence.">
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Key</th>
                                <th>Provider</th>
                                <th>Model id</th>
                                <th>Tier</th>
                                <th>$ in / out per Mtok</th>
                                <th>Calls</th>
                                <th>OK</th>
                                <th>Errors</th>
                                <th>Cached</th>
                                <th>Tokens in / out</th>
                                <th>Spend</th>
                                <th>Avg latency</th>
                                <th>Active</th>
                            </tr>
                        </thead>
                        <tbody>
                            {models.map((m) => {
                                const a = byModel[m.key]
                                return (
                                    <tr key={m.key} className={m.active ? undefined : styles.rowInactive}>
                                        <td className={styles.mono}>{m.key}</td>
                                        <td>
                                            <StatusPill tone={m.provider === 'bedrock' ? 'info' : 'neutral'} size="sm">
                                                {m.provider}
                                            </StatusPill>
                                            {m.region && <div className={styles.sub}>{m.region}</div>}
                                        </td>
                                        <td className={styles.mono} title={m.notes || undefined}>
                                            {m.modelId}
                                        </td>
                                        <td>{m.tier}</td>
                                        <td>
                                            {m.inputCostPerMtokUsd ?? '—'} / {m.outputCostPerMtokUsd ?? '—'}
                                        </td>
                                        <td>{a ? fmt(a.runs) : 0}</td>
                                        <td>{a ? fmt(a.ok) : 0}</td>
                                        <td>
                                            {a && a.errors + a.invalid > 0 ? (
                                                <StatusPill tone="bad" size="sm">
                                                    {fmt(a.errors + a.invalid)}
                                                </StatusPill>
                                            ) : (
                                                0
                                            )}
                                        </td>
                                        <td>{a ? fmt(a.cached) : 0}</td>
                                        <td className={styles.mono}>{a ? `${fmt(a.inputTokens)} / ${fmt(a.outputTokens)}` : '—'}</td>
                                        <td>{a ? pence(a.costPence) : '—'}</td>
                                        <td>{a && a.n ? `${(a.latency / a.n / 1000).toFixed(1)}s` : '—'}</td>
                                        <td>
                                            <Checkbox label="" aria-label={`${m.key} active`} checked={m.active} disabled={busy !== null || readOnly} onChange={(e) => post('Toggle model', 'comms/models/set-model-active', { key: m.key, active: e.target.checked })} />
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </Card>

            {stats.length > 0 && (
                <Card as="section" title="Breakdown by task and role">
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Task</th>
                                    <th>Model</th>
                                    <th>Role</th>
                                    <th>Calls</th>
                                    <th>OK</th>
                                    <th>Errors</th>
                                    <th>Cached</th>
                                    <th>Spend</th>
                                    <th>Avg latency</th>
                                    <th>Last call</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stats.map((s) => (
                                    <tr key={`${s.task}-${s.modelKey}-${s.role}`}>
                                        <td className={styles.mono}>{s.task}</td>
                                        <td className={styles.mono}>{s.modelKey}</td>
                                        <td>
                                            <StatusPill size="sm">{s.role}</StatusPill>
                                        </td>
                                        <td>{fmt(s.runs)}</td>
                                        <td>{fmt(s.ok)}</td>
                                        <td>{fmt(s.errors + s.invalid)}</td>
                                        <td>{fmt(s.cached)}</td>
                                        <td>{pence(s.costPence)}</td>
                                        <td>{(s.avgLatencyMs / 1000).toFixed(1)}s</td>
                                        <td className={styles.sub}>{fmtDate(s.lastCallAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}
        </div>
    )
}
